/**
 * A6: Contact context panel — collapsible right panel.
 * CRM contact card with name, email, phone, pipeline stage, tags, activity.
 * Links to full contact profile.
 */
import { useEffect, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import { useApi } from "@/hooks/use-api";
import { formatCurrency, formatDate } from "@/lib/i18n";

interface ContactPanelProps {
  conversationId: string;
}

interface ConversationContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  pipelineStage: string;
  tags: string[];
  dealValue: number | null;
  lastActivityAt: string | null;
  smsConsent?: {
    isOptedIn: boolean;
    hasExplicitPreference: boolean;
    lastUpdatedAt: string | null;
    lastAction: string | null;
    lastActionAt: string | null;
    withdrawalMethod: string | null;
  };
}

interface CommunicationMemoryDisplay {
  items: Array<{
    label: string;
    value: string;
    evidence: string;
    confidence: number;
  }>;
  totalInteractions: number;
  hasMemory: boolean;
}

const EMPTY_CONTACT: ConversationContact = {
  id: "",
  name: "—",
  email: "—",
  phone: "—",
  pipelineStage: "—",
  tags: [],
  dealValue: null,
  lastActivityAt: null,
  smsConsent: undefined,
};

function extractError(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

export function ContactPanel({ conversationId }: ContactPanelProps) {
  // GET /v1/inbox/conversations/:id/contact — mounted on api-email Worker
  const { data } = useApi<ConversationContact>(
    conversationId ? `/v1/inbox/conversations/${conversationId}/contact` : "",
    { enabled: !!conversationId },
  );
  const contact = data ?? EMPTY_CONTACT;
  const communicationMemory = useApi<CommunicationMemoryDisplay>(
    contact.id ? `/crm/people/${contact.id}/communication-memory` : "",
    { enabled: !!contact.id },
  );
  const [localSmsConsent, setLocalSmsConsent] = useState(contact.smsConsent ?? null);
  const [smsActionMessage, setSmsActionMessage] = useState<string | null>(null);
  const [isUpdatingSmsConsent, setIsUpdatingSmsConsent] = useState(false);

  useEffect(() => {
    setLocalSmsConsent(contact.smsConsent ?? null);
  }, [contact.id, contact.smsConsent?.isOptedIn, contact.smsConsent?.lastUpdatedAt, contact.smsConsent?.lastActionAt]);

  const smsConsent = localSmsConsent ?? contact.smsConsent ?? null;

  async function handleSmsOptOut() {
    setIsUpdatingSmsConsent(true);
    setSmsActionMessage(null);
    try {
      const response = await apiFetch<{
        data: {
          isOptedIn: boolean;
          alreadyOptedOut: boolean;
          lastUpdatedAt: string | null;
        };
      }>(`/v1/inbox/conversations/${conversationId}/contact/sms-opt-out`, {
        method: "POST",
      });

      setLocalSmsConsent((previous) => ({
        ...(previous ?? {}),
        isOptedIn: response.data.isOptedIn,
        hasExplicitPreference: true,
        lastUpdatedAt: response.data.lastUpdatedAt,
        lastAction: "withdrawn",
        lastActionAt: response.data.lastUpdatedAt,
        withdrawalMethod: "api",
      }));
      setSmsActionMessage(response.data.alreadyOptedOut ? "This contact is already opted out of SMS." : "SMS opt-out saved.");
    } catch (err: unknown) {
      setSmsActionMessage(extractError(err, "Could not save SMS opt-out."));
    } finally {
      setIsUpdatingSmsConsent(false);
    }
  }

  async function handleSmsOptIn() {
    setIsUpdatingSmsConsent(true);
    setSmsActionMessage(null);
    try {
      const response = await apiFetch<{
        data: {
          isOptedIn: boolean;
          alreadyOptedIn: boolean;
          lastUpdatedAt: string | null;
        };
      }>(`/v1/inbox/conversations/${conversationId}/contact/sms-opt-in`, {
        method: "POST",
      });

      setLocalSmsConsent((previous) => ({
        ...(previous ?? {}),
        isOptedIn: response.data.isOptedIn,
        hasExplicitPreference: true,
        lastUpdatedAt: response.data.lastUpdatedAt,
        lastAction: "re_granted",
        lastActionAt: response.data.lastUpdatedAt,
        withdrawalMethod: null,
      }));
      setSmsActionMessage(response.data.alreadyOptedIn ? "This contact is already opted in to SMS." : "SMS re-subscribe saved.");
    } catch (err: unknown) {
      setSmsActionMessage(extractError(err, "Could not save SMS re-subscribe."));
    } finally {
      setIsUpdatingSmsConsent(false);
    }
  }

  return (
    <div className="p-4">
      {/* Contact Header */}
      <div className="text-center mb-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-[var(--color-muted)] flex items-center justify-center text-2xl text-[var(--color-muted-foreground)]">
          {contact.name.charAt(0).toUpperCase() || "?"}
        </div>
        <h3 className="mt-2 text-sm font-semibold text-[var(--color-foreground)]">{contact.name}</h3>
        {contact.email !== "—" && (
          <p className="text-xs text-[var(--color-muted-foreground)]">{contact.email}</p>
        )}
      </div>

      {/* Contact Details */}
      <div className="space-y-3 text-sm">
        <div className="flex justify-between items-center py-1.5 border-b border-[var(--color-border)]">
          <span className="text-[var(--color-muted-foreground)]">Phone</span>
          <span className="text-[var(--color-foreground)]">{contact.phone}</span>
        </div>
        <div className="flex justify-between items-center py-1.5 border-b border-[var(--color-border)]">
          <span className="text-[var(--color-muted-foreground)]">Stage</span>
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] dark:bg-[var(--color-primary)]/90 dark:text-[var(--color-primary)]/30">
            {contact.pipelineStage}
          </span>
        </div>
        {contact.dealValue !== null && (
          <div className="flex justify-between items-center py-1.5 border-b border-[var(--color-border)]">
            <span className="text-[var(--color-muted-foreground)]">Deal value</span>
            <span className="text-[var(--color-foreground)] font-medium">
              {formatCurrency(contact.dealValue)}
            </span>
          </div>
        )}
        {contact.lastActivityAt && (
          <div className="flex justify-between items-center py-1.5 border-b border-[var(--color-border)]">
            <span className="text-[var(--color-muted-foreground)]">Last activity</span>
            <span className="text-[var(--color-foreground)] text-xs">
              {formatDate(contact.lastActivityAt)}
            </span>
          </div>
        )}
      </div>

      {/* Tags */}
      {contact.tags.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide mb-2">Tags</h4>
          <div className="flex flex-wrap gap-1">
            {contact.tags.map((tag) => (
              <span key={tag} className="inline-flex px-2 py-0.5 text-xs rounded-full bg-[var(--color-muted)] text-[var(--color-muted-foreground)]">
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {contact.phone !== "—" && (
        <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
                SMS Consent
              </h4>
              <p className="mt-1 text-sm text-[var(--color-foreground)]">
                {smsConsent?.isOptedIn === false ? "Opted out" : "Opted in"}
              </p>
              {smsConsent?.lastActionAt && (
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  Last update {formatDate(smsConsent.lastActionAt)}
                </p>
              )}
            </div>
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                smsConsent?.isOptedIn === false
                  ? "bg-destructive/10 text-destructive"
                  : "bg-[var(--color-success)]/10 text-[var(--color-success)]"
              }`}
            >
              {smsConsent?.isOptedIn === false ? "Blocked" : "Reachable"}
            </span>
          </div>

          {smsActionMessage && (
            <p className="mt-3 text-xs text-[var(--color-muted-foreground)]">{smsActionMessage}</p>
          )}

          {smsConsent?.isOptedIn === false ? (
            <button
              type="button"
              onClick={() => void handleSmsOptIn()}
              disabled={isUpdatingSmsConsent}
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-[var(--color-success)]/40 px-3 py-2 text-sm font-medium text-[var(--color-success)] hover:bg-[var(--color-success)]/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUpdatingSmsConsent ? "Saving re-subscribe..." : "Re-subscribe SMS"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleSmsOptOut()}
              disabled={isUpdatingSmsConsent}
              className="mt-3 inline-flex w-full items-center justify-center rounded-lg border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/5 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUpdatingSmsConsent ? "Saving opt-out..." : "Opt out SMS"}
            </button>
          )}
        </div>
      )}

      <div className="mt-4">
        <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-muted-foreground)]">
          Communication Memory
        </h4>
        {communicationMemory.data?.hasMemory ? (
          <div className="space-y-2">
            {communicationMemory.data.items.slice(0, 3).map((item) => (
              <div key={`${item.label}-${item.value}`} className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs font-semibold text-[var(--color-muted-foreground)]">{item.label}</span>
                  <span className="text-[10px] text-[var(--color-muted-foreground)]">
                    {Math.round(item.confidence * 100)}%
                  </span>
                </div>
                <div className="mt-1 text-sm text-[var(--color-foreground)]">{item.value}</div>
                <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{item.evidence}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-background)] px-3 py-3 text-xs text-[var(--color-muted-foreground)]">
            We will surface communication memory once this contact has enough reply and activity history.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-6 space-y-2">
        <a
          href={contact.id ? `/crm/contacts/${contact.id}` : "#"}
          className="block w-full text-center px-3 py-2 text-sm font-medium text-[var(--color-primary)] border border-[var(--color-primary)] rounded-lg hover:bg-[var(--color-primary)]/5 dark:text-[var(--color-primary)]/70 dark:border-[var(--color-primary)]/70 dark:hover:bg-[var(--color-primary)]/20"
        >
          View full profile
        </a>
      </div>
    </div>
  );
}
